import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { OperationWizardComponent } from './operation-wizard.component';
import { OperationWizardService } from './operation-wizard.service';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { CorporateOperationsService } from '../../../../core/services/clients/corporate-operations.service';
import { CorporateOperation } from '../../../../core/models/corporate-operation.model';
import { CLIENT_TYPES, CLIENT_RELATION_TYPES } from '../../../../core/constants/client.constants';
import { OperacionCategoria, OperacionSubtipo, OperacionSocietaria } from '../mock-data/operaciones.mock';

@Component({
  selector: 'app-operation-wizard-page',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, OperationWizardComponent],
  templateUrl: './operation-wizard-page.component.html',
  styleUrl: './operation-wizard-page.component.scss',
})
export class OperationWizardPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly wizardService = inject(OperationWizardService);
  private readonly clientsService = inject(ClientsService);
  private readonly corporateOpsService = inject(CorporateOperationsService);

  readonly faChevronLeft = faChevronLeft;

  categoria: OperacionCategoria | null = null;
  subtipo: OperacionSubtipo | null = null;
  clientId = 0;
  /** Set when editing an existing operation */
  existingOperation = signal<CorporateOperation | null>(null);

  client = signal<any>(null);
  clientName = signal('Cargando...');
  clientTypeObj = signal<any>(null);
  clientRelationObj = signal<any>(null);

  private readonly personTypes = CLIENT_TYPES;
  private readonly relationTypes = CLIENT_RELATION_TYPES;

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    const routeParams = this.route.snapshot.paramMap;

    this.categoria = (params.get('categoria') as OperacionCategoria) || null;
    this.subtipo = (params.get('subtipo') as OperacionSubtipo) || null;
    this.clientId = Number(routeParams.get('id')) || 0;

    const operationId = params.get('operationId') ? Number(params.get('operationId')) : null;

    await Promise.all([
      this.loadClient(),
      operationId ? this.loadExistingOperation(operationId) : Promise.resolve(),
    ]);
  }

  private async loadClient(): Promise<void> {
    if (!this.clientId) return;
    try {
      const c = await this.clientsService.getClientById(this.clientId);
      if (c) {
        this.client.set(c);
        const name = c.personType === 'PHYSICAL_PERSON'
          ? [c.firstSurname, c.secondSurname ? c.secondSurname + ',' : null, c.name].filter(Boolean).join(' ')
          : (c.name || '');
        this.clientName.set(name || 'Cliente');
        this.clientTypeObj.set(this.personTypes.find(p => p.value === c.personType) ?? null);
        this.clientRelationObj.set(this.relationTypes.find(r => r.value === c.relationType) ?? null);
      }
    } catch {
      this.clientName.set('Cliente');
    }
  }

  private async loadExistingOperation(id: number): Promise<void> {
    try {
      const op = await this.corporateOpsService.getOne(id);
      this.existingOperation.set(op);
      // Ensure categoria/subtipo match the existing operation
      if (op.categoria) this.categoria = op.categoria as OperacionCategoria;
      if (op.subtipo) this.subtipo = op.subtipo as OperacionSubtipo;
    } catch (e) {
      console.error('Error loading existing operation for edit', e);
    }
  }

  onCancel(): void {
    this.router.navigate(['/clients/detail', this.clientId], {
      queryParams: { clientTab: 'operacionesSocietarias' },
    });
  }

  onComplete(op: OperacionSocietaria): void {
    this.wizardService.setResult(op);
    this.router.navigate(['/clients/detail', this.clientId], {
      queryParams: { clientTab: 'operacionesSocietarias' },
    });
  }
}
